'use client';

import Link from 'next/link';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  baseUrl: string;
  searchParams?: Record<string, string | string[] | undefined>;
  pageParamOffset?: number;
}

export const Pagination = ({
  currentPage,
  totalPages,
  baseUrl,
  searchParams = {},
  pageParamOffset = 0,
}: PaginationProps) => {
  if (totalPages <= 1) {
    return null;
  }

  const buildUrl = (page: number) => {
    const params = new URLSearchParams();

    // Add existing search params
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          value.forEach((v) => { params.append(key, v); });
        } else {
          params.set(key, value);
        }
      }
    });

    // Add page param (only if not first page)
    if (page > 0) {
      params.set('page', (page + pageParamOffset).toString());
    }

    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  };

  // Calculate page range to show
  const getPageRange = () => {
    const delta = 2;
    const range: number[] = [];
    const rangeWithDots: (number | 'dots')[] = [];

    for (
      let i = Math.max(0, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    // Add first page if not in range
    if (range[0] > 0) {
      rangeWithDots.push(0);
      if (range[0] > 1) {
        rangeWithDots.push('dots');
      }
    }

    rangeWithDots.push(...range);

    // Add last page if not in range
    if (range[range.length - 1] < totalPages - 1) {
      if (range[range.length - 1] < totalPages - 2) {
        rangeWithDots.push('dots');
      }
      rangeWithDots.push(totalPages - 1);
    }

    return rangeWithDots;
  };

  const pageRange = getPageRange();

  return (
    <nav
      className="flex items-center justify-center gap-2"
      aria-label="Pagination"
    >
      {/* Previous Button */}
      {currentPage > 0 ? (
        <Link
          href={buildUrl(currentPage - 1)}
          className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          <span>Previous</span>
        </Link>
      ) : (
        <span className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-400 bg-gray-50 border border-gray-100 rounded-lg cursor-not-allowed">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          <span>Previous</span>
        </span>
      )}

      {/* Page Numbers */}
      <div className="hidden sm:flex items-center gap-1">
        {pageRange.map((item, index) => {
          return item === 'dots' ? (
            <span
              key={`dots-${index}`}
              className="px-3 py-2 text-sm text-gray-400"
            >
              ...
            </span>
          ) : (
            <Link
              key={item}
              aria-current={item === currentPage ? 'page' : undefined}
              href={buildUrl(item)}
              className={`min-w-[40px] h-10 inline-flex items-center justify-center text-sm font-medium rounded-lg transition-colors ${
                item === currentPage
                  ? 'bg-[var(--ra-green)] text-white shadow-sm'
                  : 'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
              }`}
            >
              <span>{item + 1}</span>
            </Link>
          );
        })}
      </div>

      {/* Current page indicator for mobile */}
      <span className="sm:hidden px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg">
        {`${currentPage + 1} / ${totalPages}`}
      </span>

      {/* Next Button */}
      {currentPage < totalPages - 1 ? (
        <Link
          href={buildUrl(currentPage + 1)}
          className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
        >
          <span>Next</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      ) : (
        <span className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-400 bg-gray-50 border border-gray-100 rounded-lg cursor-not-allowed">
          <span>Next</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </span>
      )}
    </nav>
  );
};
